import * as React from 'react';

import { Spinner } from '../Spinner';
import { tv, VariantProps } from 'tailwind-variants';

const styles = tv({
  base: 'flex items-center justify-center gap-x-2 rounded-md font-medium hover:opacity-80 focus:outline-none disabled:cursor-not-allowed disabled:opacity-70',
  variants: {
    // Note: `primary`/`secondary` here are inverted relative to the Figma
    // design system's own Primary (#0C4552, `rw-sub`) / Secondary (#377E7F,
    // `primary-teal`) naming. Left as-is rather than swapped, since swapping
    // would silently recolor every <Button> in the app that relies on the
    // `primary` default (~28 call sites) without a per-screen visual review.
    color: {
      primary: 'bg-primary-teal text-white',
      secondary: 'bg-rw-sub text-white',
      inverse: 'bg-white text-primary-teal',
      danger: 'bg-rw-red text-white',
      dark: 'bg-primary-chic-teal text-white',
      transparent:
        'border-none bg-transparent text-rw-neutral-gray !shadow-none',
      // Figma's Success color reuses Secondary teal 1:1 (see Design System
      // Guidelines > Color > User Interface > Success (=Secondary)).
      success: 'bg-primary-teal text-white',
      // Figma's Warning (#FFD037) is only AA-contrast with dark text, not
      // white (see Design System Guidelines > Color > User Interface > Warning).
      warning: 'bg-rw-yellow text-rw-black-gray',
    },
    size: {
      xs: 'gap-x-1 p-1 text-xs',
      sm: 'px-4 py-2 text-sm',
      md: 'px-6 py-2 text-base',
      lg: 'px-8 py-3 text-lg',
    },
    noBordered: {
      false: 'border border-rw-neutral-lighter-gray shadow-sm',
    },
  },
  defaultVariants: {
    color: 'primary',
    size: 'md',
    noBordered: false,
  },
});

const designSystemStyles = tv({
  base: 'inline-flex items-center justify-center gap-x-2 rounded-full font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-teal focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60',
  variants: {
    variant: {
      primary: 'bg-rw-sub text-white hover:bg-rw-dark-blue',
      secondary:
        'border-2 border-rw-neutral-lighter-gray bg-rw-neutral-off-white text-rw-neutral-dark-gray hover:bg-rw-neutral-lighter-gray',
      ghost:
        'border-2 border-transparent bg-transparent text-rw-neutral-dark-gray hover:bg-rw-neutral-off-white',
      danger: 'bg-rw-red text-white hover:bg-red-600',
    },
    size: {
      xs: 'h-5 px-2 text-xs leading-4',
      sm: 'h-7 px-2.5 text-base leading-6',
      md: 'h-8 px-3 text-base leading-6',
      lg: 'h-10 px-4 text-base leading-6',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
});

export type ButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'color'
> &
  VariantProps<typeof styles> & {
    isLoading?: boolean;
    startIcon?: React.ReactElement;
    endIcon?: React.ReactElement;
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  };

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = 'button',
      className,
      color,
      size,
      noBordered,
      variant,
      isLoading = false,
      startIcon,
      endIcon,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        data-button-variant={variant}
        className={
          variant
            ? designSystemStyles({ variant, size, className })
            : styles({ color, size, noBordered, className })
        }
        {...props}
      >
        {isLoading && <Spinner size="sm" className="text-current" />}
        {!isLoading && startIcon}
        <span>{props.children}</span> {!isLoading && endIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';
